import { type FC, memo, useCallback, useContext, useState } from 'react';
import { LoaderCircle } from 'lucide-react';
import { type Agent } from '@matrix-forge/graphql-client';
import { LlmDocumentCardLayout } from '@matrix-forge/ui/custom/Chat/LlmDocumentCardLayout';
import { type ForwardableDocumentProps } from '@matrix-forge/ui/contexts/ForwardDocumentContext';
import { Button } from '@matrix-forge/ui/components/button';
import { Textarea } from '@matrix-forge/ui/components/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@matrix-forge/ui/components/dialog';
import { AgentsContext } from '@/contexts/AgentsContext';
import { AgentSelect } from '@/components/AgentSelect';
import { useNewChatAndNavigate } from '@/hooks/useNewChatAndNavigate';

type ForwardDocumentModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  document?: ForwardableDocumentProps | null;
  onSubmit?: (input: { agentId: string; prompt: string }) => any;
  loading?: boolean;
  allAgents?: Agent[] | null;
};

/** Renders a multi-tab modal dialog for creating/editing a job configuration. */
export const ForwardDocumentModal = ({
  isOpen,
  onOpenChange,
  document,
  onSubmit,
  loading,
  allAgents,
}: ForwardDocumentModalProps) => {
  const [prompt, setPrompt] = useState('Please summarize the document below.');
  const [selectedAgentId, setSelectedAgentId] = useState(allAgents?.[0]?.id || '');
  const handleSubmit = () => {
    if (!selectedAgentId) {
      return;
    }
    const docBlock = document
      ? `\n\n<PRIVATE_INSTRUCTIONS><FORWARDED_DOCUMENT>${JSON.stringify({ title: document.title, type: document.type })}</FORWARDED_DOCUMENT>\nThe document's contents are below for your reference:\n\n${document.content || 'Document is empty.'}</PRIVATE_INSTRUCTIONS>`
      : '';
    onSubmit?.({
      agentId: selectedAgentId,
      prompt: prompt + docBlock,
    })?.then(() => {
      onOpenChange(false);
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl px-0">
        <DialogHeader className="px-6">
          <DialogTitle>Start a New Chat</DialogTitle>
          <DialogDescription>
            Choose a persona to start a new conversation about the selected document.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-auto w-full max-h-[60vh] overflow-y-auto px-6 py-4 border-y border-border space-y-4">
          <div className="space-y-2">
            <div className="text-muted-foreground">Persona</div>
            <AgentSelect
              value={selectedAgentId}
              onValueChange={setSelectedAgentId}
              agents={allAgents}
            />
          </div>
          <div className="space-y-2">
            <div className="text-muted-foreground">Message</div>
            <Textarea
              value={prompt}
              className="w-full resize-none"
              onChange={e => setPrompt(e.target.value)}
              rows={5}
              autoFocus
            />
          </div>
          {!!document && (
            <div className="space-y-2">
              <div className="text-muted-foreground">Document</div>
              <LlmDocumentCardLayout title={document.title || 'Document'} type={document.type} />
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 px-6">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!selectedAgentId}>
            {loading ? <LoaderCircle className="animate-spin" /> : 'Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ForwardDocumentModalMemoized = memo(ForwardDocumentModal);

type ForwardDocumentModalConnectedProps = Omit<ForwardDocumentModalProps, 'onSubmit' | 'allAgents'>;

/* Wires up ForwardDocumentModal onSave prop to a mutation call and a toast. */
export const ForwardDocumentModalConnected: FC<ForwardDocumentModalConnectedProps> = props => {
  const allAgents = useContext(AgentsContext)?.agents || [];
  const [newChat, { loading }] = useNewChatAndNavigate();

  const onSubmit = useCallback(
    ({ agentId, prompt }: { agentId: string; prompt: string }) => {
      return newChat?.({ agentId, prompt }).then(() => {
        props.onOpenChange(false);
      });
    },
    [newChat]
  );
  return (
    <ForwardDocumentModalMemoized
      {...props}
      allAgents={allAgents}
      loading={loading}
      onSubmit={onSubmit}
    />
  );
};

/** Forces the ForwardDocumentModal to be unmounted each time it is closed. */
export const ForwardDocumentModalContainer: FC<ForwardDocumentModalConnectedProps> = props =>
  !!props.isOpen && <ForwardDocumentModalConnected {...props} />;
